<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import KwhSlider from './KwhSlider.vue'
import ChargeRateInput from './ChargeRateInput.vue'
import DeadlinePicker from './DeadlinePicker.vue'
import ZoneToggle from './ZoneToggle.vue'
import AnnualKmSelect from './AnnualKmSelect.vue'
import Accordion from './Accordion.vue'
import {
  calculateSavings,
  calculateAnnualSavings,
  DEFAULT_CHARGE_RATE_KW,
  DEFAULT_CONSUMPTION_KWH_PER_100KM,
} from '../lib/calculateSavings'
import { parseDeadline, eligibleHours, formatDeadline } from '../lib/scheduleWindow'
import { formatKr, formatHourList } from '../lib/format'
import { useCountAnimation } from '../composables/useCountAnimation'
import type { HourPrice, PriceZone } from '@greenbow/composables'

const props = defineProps<{
  today: readonly HourPrice[]
  tomorrow?: readonly HourPrice[] | null
  currentHour: number
  zone?: PriceZone
  initialKwh?: number
  initialChargeRate?: number
  initialKmPerYear?: number
  initialDeadline?: string
  initialShowAnnual?: boolean
}>()

export interface OptimalSlot {
  hour: number
  dayOffset: 0 | 1
}

export interface CalculatorStateSnapshot {
  chargeRate: number
  kwh: number
  kmPerYear: number
  deadline: string
  showAnnual: boolean
}

const emit = defineEmits<{
  'zone-change': [zone: PriceZone]
  'optimal-slots-change': [slots: OptimalSlot[]]
  'state-change': [state: CalculatorStateSnapshot]
}>()

const kwh = ref(props.initialKwh ?? 50)
const chargeRate = ref(props.initialChargeRate ?? DEFAULT_CHARGE_RATE_KW)
const deadlineTime = ref(props.initialDeadline ?? '07:00')
const kmPerYear = ref(props.initialKmPerYear ?? 15000)
const showAnnual = ref(props.initialShowAnnual ?? false)

const tomorrowAvailable = computed(() => !!props.tomorrow && props.tomorrow.length > 0)

const deadline = computed(() =>
  parseDeadline(deadlineTime.value, props.currentHour, tomorrowAvailable.value),
)

const deadlineLabel = computed(() => formatDeadline(deadline.value))

const eligible = computed(() =>
  eligibleHours(props.today, props.tomorrow ?? null, props.currentHour, deadline.value),
)

const result = computed(() => calculateSavings(eligible.value, kwh.value, chargeRate.value))

const hasData = computed(() => eligible.value.length > 0)

const hoursNeeded = computed(() => Math.ceil(kwh.value / chargeRate.value))

const notEnoughHours = computed(() => hasData.value && eligible.value.length < hoursNeeded.value)

const annual = computed(() =>
  calculateAnnualSavings(result.value, kmPerYear.value, DEFAULT_CONSUMPTION_KWH_PER_100KM),
)

const sessionsPerYearRounded = computed(() => Math.round(annual.value.sessionsPerYear))

// Slot-machine counter for the hero savings number.
const annualSavingsTarget = computed(() => annual.value.annualSavings)
const animatedAnnualSavings = useCountAnimation(annualSavingsTarget)

// Chronologically sorted optimal hours → slots with dayOffset.
// Detects today→tomorrow transition by watching for hour wrap-around
// (since optimalHours is already sorted chronologically).
const optimalSlots = computed<OptimalSlot[]>(() => {
  const hours = result.value.optimalHours
  if (hours.length === 0) return []

  const slots: OptimalSlot[] = []
  const firstHour = hours[0]!
  let dayOffset: 0 | 1 = firstHour >= props.currentHour ? 0 : 1

  for (let i = 0; i < hours.length; i++) {
    const hour = hours[i]!
    if (i > 0 && hour < hours[i - 1]!) {
      // Hour numbers decreased → we crossed midnight
      dayOffset = 1
    }
    slots.push({ hour, dayOffset })
  }
  return slots
})

watch(
  optimalSlots,
  (slots) => {
    emit('optimal-slots-change', slots)
  },
  { immediate: true, deep: true },
)

const stateSnapshot = computed<CalculatorStateSnapshot>(() => ({
  chargeRate: chargeRate.value,
  kwh: kwh.value,
  kmPerYear: kmPerYear.value,
  deadline: deadlineTime.value,
  showAnnual: showAnnual.value,
}))

watch(stateSnapshot, (state) => emit('state-change', state), { immediate: true, deep: true })
</script>

<template>
  <section class="savings-calculator" aria-labelledby="savings-calculator-heading">
    <header class="savings-calculator__header">
      <div class="savings-calculator__title-row">
        <h2 id="savings-calculator-heading" class="savings-calculator__heading">
          Beregn din besparelse med Greenbow SmartCharge
        </h2>
        <ZoneToggle v-if="zone" :zone="zone" @change="(next) => $emit('zone-change', next)" />
      </div>
      <p class="savings-calculator__lead">
        Justér ladehastighed, kWh-behov og deadline for at se hvor meget du kan spare ved at lade på
        de billigste timer.
      </p>
    </header>

    <div class="savings-calculator__inputs">
      <ChargeRateInput v-model="chargeRate" />
      <DeadlinePicker v-model="deadlineTime" :resolved-label="deadlineLabel" />
    </div>

    <KwhSlider v-model="kwh" />

    <p v-if="deadline.isFallback" class="savings-calculator__info" role="status">
      Morgendagens priser er endnu ikke tilgængelige — beregningen dækker i dag frem til kl. 23:00.
    </p>

    <p v-if="notEnoughHours" class="savings-calculator__warning" role="alert">
      Der er kun {{ eligible.length }} tilgængelige timer inden dit valgte tidspunkt — det er ikke
      nok til at dække {{ kwh }} kWh ved {{ chargeRate }} kW. Vælg en senere deadline, færre kWh
      eller højere ladehastighed.
    </p>

    <div class="savings-calculator__results" aria-live="polite">
      <div class="savings-calculator__card">
        <span class="savings-calculator__card-label">Pris med SmartCharge</span>
        <span class="savings-calculator__card-value">
          {{ hasData ? formatKr(result.smartCost) : '—' }}
        </span>
      </div>
      <div class="savings-calculator__card">
        <span class="savings-calculator__card-label">Pris uden SmartCharge</span>
        <span class="savings-calculator__card-value">
          {{ hasData ? formatKr(result.averageCost) : '—' }}
        </span>
      </div>
      <div class="savings-calculator__card savings-calculator__card--highlight">
        <span class="savings-calculator__card-label">Besparelse med SmartCharge</span>
        <span class="savings-calculator__card-value">
          <template v-if="hasData">+{{ formatKr(result.savings) }}</template>
          <template v-else>—</template>
        </span>
      </div>
    </div>

    <div v-if="hasData" class="savings-calculator__annual-toggle-row">
      <label class="savings-calculator__annual-toggle">
        <input
          type="checkbox"
          class="savings-calculator__annual-toggle-input"
          :checked="showAnnual"
          @change="showAnnual = ($event.target as HTMLInputElement).checked"
        />
        <span class="savings-calculator__annual-toggle-track" aria-hidden="true">
          <span class="savings-calculator__annual-toggle-thumb" />
        </span>
        <span class="savings-calculator__annual-toggle-label">Vis årlig besparelse</span>
      </label>
    </div>

    <div
      v-if="hasData"
      class="savings-calculator__annual-reveal"
      :class="{ 'savings-calculator__annual-reveal--open': showAnnual }"
      :inert="!showAnnual"
      :aria-hidden="!showAnnual"
    >
      <div class="savings-calculator__annual-reveal-inner">
        <div class="savings-calculator__annual-config">
          <AnnualKmSelect v-model="kmPerYear" />
        </div>
        <section class="savings-calculator__annual" aria-labelledby="annual-savings-heading">
          <h3 id="annual-savings-heading" class="savings-calculator__annual-heading">
            Årlig besparelse
          </h3>
          <p class="savings-calculator__annual-value">+{{ formatKr(animatedAnnualSavings) }}</p>
          <p class="savings-calculator__annual-meta">
            Ved {{ annual.kmPerYear.toLocaleString('da-DK') }} km om året og dagens spotpriser —
            svarer til ≈ {{ sessionsPerYearRounded }} opladninger à {{ kwh }} kWh
          </p>
        </section>
      </div>
    </div>

    <p v-if="hasData && result.optimalHours.length > 0" class="savings-calculator__hours">
      <strong>Billigste ladetider:</strong> {{ formatHourList(result.optimalHours) }}
    </p>

    <Accordion class="savings-calculator__disclaimer">
      <template #summary>
        <span class="savings-calculator__disclaimer-title">Hvordan er besparelsen beregnet?</span>
      </template>
      <div class="savings-calculator__disclaimer-body">
        <p>
          Estimeret besparelse baseret på spotpriser alene. Din faktiske besparelse kan variere
          afhængigt af dit netselskab og gældende tariffer.
        </p>
        <p>
          Den årlige besparelse er projiceret ved at antage at dagens prismønster gentager sig året
          rundt, og at bilen kører ca. 5 km pr. kWh. I praksis varierer spotpriser fra måned til
          måned — tallet skal ses som et overslag, ikke en garanti.
        </p>
        <p>
          <strong>Med en elaftale hos Greenbow</strong> tager vi højde for nettarif og afgifter i
          din ladeplan — særligt nettariffen har stor betydning for den samlede elpris.
        </p>
      </div>
    </Accordion>
  </section>
</template>

<style lang="scss" scoped>
.savings-calculator {
  display: flex;
  flex-direction: column;
  gap: space(5);
  padding: space(6);
  background: color('white');
  border: 1px solid color('background-medium');
  border-radius: $radius-lg;

  &__header {
    display: flex;
    flex-direction: column;
    gap: space(2);
  }

  &__title-row {
    display: flex;
    flex-direction: column;
    gap: space(3);
    align-items: flex-start;

    @include media-up(sm) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__heading {
    margin: 0;
    font-size: $font-size-xl;
    color: color('primary');
  }

  &__lead {
    margin: 0;
    color: color('ink-light');
    font-size: $font-size-sm;
  }

  &__inputs {
    display: grid;
    grid-template-columns: 1fr;
    gap: space(5);

    @include media-up(sm) {
      grid-template-columns: auto auto;
      justify-content: start;
      gap: space(8);
    }
  }

  &__info {
    margin: 0;
    padding: space(3) space(4);
    background: color('background');
    border-left: 3px solid color('primary-light');
    border-radius: $radius-sm;
    color: color('ink-light');
    font-size: $font-size-sm;
    font-style: italic;
  }

  &__warning {
    margin: 0;
    padding: space(3) space(4);
    background: color('warning-light');
    border-left: 3px solid color('warning');
    border-radius: $radius-sm;
    color: color('ink');
    font-size: $font-size-sm;
  }

  &__results {
    display: grid;
    grid-template-columns: 1fr;
    gap: space(3);

    @include media-up(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: space(1);
    padding: space(4);
    background: color('background');
    border-radius: $radius-md;

    &--highlight {
      background: color('success-light');
      border: 2px solid color('accent');
    }
  }

  &__card-label {
    font-size: $font-size-xs;
    font-weight: $font-weight-medium;
    color: color('ink-light');
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wide;
  }

  &__card-value {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    color: color('primary');
  }

  &__card--highlight &__card-value {
    color: color('accent-dark');
  }

  &__annual-toggle-row {
    display: flex;
    justify-content: flex-start;
  }

  &__annual-toggle {
    display: inline-flex;
    align-items: center;
    gap: space(3);
    cursor: pointer;
    user-select: none;
  }

  &__annual-toggle-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  &__annual-toggle-track {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    background: color('background-light');
    border-radius: $radius-pill;
    transition: background $duration-base $easing-standard;
  }

  &__annual-toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: color('white');
    border-radius: 50%;
    box-shadow: $shadow-sm;
    transition: transform $duration-base $easing-standard;
  }

  &__annual-toggle-input:checked + &__annual-toggle-track {
    background: color('accent');
  }

  &__annual-toggle-input:checked + &__annual-toggle-track &__annual-toggle-thumb {
    transform: translateX(16px);
  }

  &__annual-toggle-input:focus-visible + &__annual-toggle-track {
    outline: 2px solid color('accent');
    outline-offset: 2px;
  }

  &__annual-toggle-label {
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    color: color('ink');
  }

  &__annual-reveal {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows $duration-slow $easing-standard;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &__annual-reveal--open {
    grid-template-rows: 1fr;
  }

  &__annual-reveal-inner {
    display: flex;
    flex-direction: column;
    gap: space(5);
    overflow: hidden;
    min-height: 0;
  }

  &__annual-config {
    display: flex;
    justify-content: flex-start;
  }

  &__annual {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space(3);
    padding: space(10) space(6);
    background: color('white');
    border-radius: $radius-lg;
    box-shadow: $shadow-md;
    text-align: center;

    @include media-up(sm) {
      padding: space(12) space(8);
    }
  }

  &__annual-heading {
    margin: 0;
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    color: color('primary');
    letter-spacing: $letter-spacing-tight;
  }

  &__annual-value {
    margin: 0;
    font-size: $font-size-4xl;
    font-weight: $font-weight-extrabold;
    color: color('accent');
    letter-spacing: $letter-spacing-tight;
    line-height: $line-height-tight;
  }

  &__annual-meta {
    max-width: 420px;
    margin: 0;
    font-size: $font-size-sm;
    color: color('ink-light');
    line-height: $line-height-snug;
  }

  &__hours {
    margin: 0;
    padding: space(3) space(4);
    background: color('background');
    border-radius: $radius-md;
    font-size: $font-size-sm;
    color: color('ink');
  }

  &__disclaimer {
    background: color('background');
    border: 1px solid color('background-medium');
    border-radius: $radius-md;
    font-size: $font-size-sm;
    line-height: $line-height-snug;
    color: color('ink');

    :deep(.accordion__summary) {
      padding: space(3) space(4);
      color: color('primary');
      font-weight: $font-weight-semibold;
    }

    :deep(.accordion__summary:hover) {
      color: color('accent-dark');
    }
  }

  &__disclaimer-title {
    flex: 1;
  }

  &__disclaimer-body {
    display: flex;
    flex-direction: column;
    gap: space(3);
    padding: space(3) space(4) space(4);
    border-top: 1px solid color('background-medium');

    p {
      margin: 0;
    }

    strong {
      color: color('accent-dark');
    }
  }
}
</style>
